<script setup lang="ts">
import type { SbBlokData } from '@storyblok/vue';

interface Blok {
    _uid: string;
    content: any;
    image?: {
        filename: string;
    };
    title: string;
    additionalBlocks?: SbBlokData[];
    first_published_at?: string;
    published_at?: string;
    created_at: string;
}

const props = withDefaults(
    defineProps<{
        blok: Blok;
    }>(),
    {
        blok: () =>
            ({
                _uid: '',
                content: {},
                title: '',
                additionalBlocks: [],
                created_at: new Date().toISOString(),
            }) as Blok,
    },
);

const date = new Date(
    props.blok.first_published_at || props.blok.published_at || props.blok.created_at,
);

const articleRendered = computed(() => {
    return renderRichText(props.blok.content);
});
</script>

<template>
    <main class="pb-16 lg:pb-24 bg-white dark:bg-midnight-blue antialiased">
        <section
            ref="root"
            :class="`relative bg-no-repeat bg-cover bg-center bg-gray-300 bg-blend-multiply`"
            :style="`background-image: url('${blok.image?.filename}')`"
        >
            <div class="absolute inset-0" style="mix-blend-mode: multiply"></div>
            <div
                class="bg-gradient-to-t from-[rgba(0,0,0,0.8)] bg-blend-multiply h-[500px] relative flex items-center justify-center"
            >
                <div class="absolute max-w-3xl bottom-0 mx-auto pb-8 px-8 text-center">
                    <h1 class="mb-4 text-5xl leading-none text-white font-bold">
                        {{ blok.title }}
                    </h1>
                    <p class="mb-8 text-lg font-normal text-white lg:text-xl">
                        {{ blok.published_at ? $t('blog.published') : $t('blog.created') }}
                        {{
                            $d(date, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                            })
                        }}
                    </p>
                </div>
            </div>
        </section>

        <div class="flex justify-between mt-12 px-4 mx-auto max-w-screen-xl">
            <article
                class="mx-auto w-full max-w-3xl format format-sm sm:format-base lg:format-lg format-blue dark:format-invert"
            >
                <ClientOnly>
                    <!-- TODO Why is ClientOnly necessary here? -->
                    <p v-html="articleRendered"></p>
                </ClientOnly>
            </article>
        </div>
    </main>

    <aside v-for="additionalBlok in props.blok.additionalBlocks">
        <StoryblokComponent
            v-if="additionalBlok.component === 'featured-articles-grid'"
            :blok="{
                ...additionalBlok,
                // Just to make sure we don't include the current article in any related articles
                excludeIds: [blok._uid],
            }"
        />
        <StoryblokComponent v-else :blok="additionalBlok" />
    </aside>
</template>
